import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { useAuthenticationStore } from '~/auth/useAuthenticationStore';
import { I18nProvider } from '~/common/contexts/I18nProvider';
import { isDev } from '~/dev/utils/isDev';
import { useDynamicallyLoadMockServiceIfEnabled } from '~/dev/utils/MockService/useDynamicallyLoadMockServiceIfEnabled';
import { initialize as initializeEds } from '~/eds/src/system';
import { BootErrorPage } from '~/features/app/BootErrorPage';
import { EdsNumberFormatProvider } from '~/features/app/EdsNumberFormatProvider';
import { MaintenancePage } from '~/features/app/MaintenancePage';
import { useSidebarNavFavicon } from '~/features/app/useSidebarNavFavicon';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useFlagsReady } from '~/flags/useFlagsReady';
import { useAsyncLanguageLoader } from '~/i18n/hooks/useAsyncLanguageLoader';
import { slices } from '~/redux/slices';
import { enterClientSlug, useClientSlug } from '~/routing/clientSlug';
import { parseWorkspace } from '~/routing/utils';
import { BootMark, markBootOnce } from '~/utils/bootTiming';

import SplashPage from '../SplashPage/SplashPage';
import { AppDataRouter } from './AppDataRouter';
import AuthSessionBootstrap from './AuthSessionBootstrap';

initializeEds();

function App() {
  const isMockReady = useDynamicallyLoadMockServiceIfEnabled();
  useSidebarNavFavicon();

  const language = useSelector(slices.i18n.selectors.selectLanguage);
  const userCurrency = useSelector(slices.i18n.selectors.selectCurrency);
  const currentUser = useSelector(
    slices.currentUser.selectors.selectCurrentUser,
  );

  const { pathname } = window.location;
  const { isLoggedIn: isUserLoggedIn } = useAuthenticationStore();
  const enableReady = !(
    !pathname.includes('folders-permission-check') &&
    !isDev &&
    isUserLoggedIn
  );
  const [resolvedSubdomain, setSubdomain] = useState('');
  const [hasCurrentUserFetchFailed, setHasCurrentUserFetchFailed] =
    useState(false);
  const [hasFlagsFailed, setHasFlagsFailed] = useState(false);
  const handleCurrentUserFetchFailed = useCallback(
    () => setHasCurrentUserFetchFailed(true),
    [],
  );
  const handleFlagsFailed = useCallback(() => setHasFlagsFailed(true), []);
  const rawSubdomain = useClientSlug();
  // Only a logged-in session has to wait for the API-confirmed subdomain; a
  // clientless visitor's workspace is whatever the URL already says, so taking
  // it straight from the URL keeps the splash gate below from flashing when
  // `enterClientSlug` swaps in a tenant prefix mid-login.
  const subdomain =
    resolvedSubdomain || (isUserLoggedIn ? '' : parseWorkspace(rawSubdomain));

  // When a logged-in user lands on the root URL with no workspace prefix (e.g.
  // localhost:3000/ or clients.evisort.com/), the React Router would normally
  // route through PrivateRoute → AuthenticatedRoute → /documents → /unauthorized
  // before AuthSessionBootstrap's checkSubDomain fires the real redirect.
  // Instead, hold on the splash screen and swap in the workspace prefix as soon
  // as the user's subdomain resolves from the API — no intermediate flashes.
  useEffect(() => {
    if (subdomain && isUserLoggedIn && pathname === '/') {
      enterClientSlug(subdomain, `/${subdomain}/`);
    }
  }, [subdomain, isUserLoggedIn, pathname]);

  const isReady = useFlagsReady({
    enableReady,
    onFailed: handleFlagsFailed,
    user: currentUser,
  });
  const isUnderMigrationMaintenance = useFlag(
    FlagType.UnifiedFieldsMigrationMaintenance,
  );
  const { messages, isLoading: isLocaleLoading } =
    useAsyncLanguageLoader(language);

  const hasCurrentUser = Object.keys(currentUser).length > 0;
  const shouldInitialize = !hasCurrentUser && pathname !== '/';
  const hasBootFailed =
    hasFlagsFailed || (hasCurrentUserFetchFailed && !hasCurrentUser);

  const showSplash =
    !isMockReady ||
    !isReady ||
    isLocaleLoading ||
    // Logged-in user at a bare root URL (no workspace prefix): block the React
    // Router tree until the useEffect above fires the hard redirect to their
    // workspace. Without this gate the router would flash /documents →
    // /unauthorized before AuthSessionBootstrap's subdomain check catches up.
    (isUserLoggedIn && pathname === '/') ||
    (!subdomain &&
      !pathname.includes('folders-permission-check') &&
      rawSubdomain &&
      (isUserLoggedIn || shouldInitialize));

  // The router — and therefore any page's own data fetching — cannot mount
  // until this gate opens, so it is the single most useful boot milestone.
  if (!showSplash) {
    markBootOnce(BootMark.SplashGateOpen);
  }

  let content = <AppDataRouter />;
  if (showSplash) {
    content = hasBootFailed ? <BootErrorPage /> : <SplashPage />;
  } else if (isUnderMigrationMaintenance) {
    content = <MaintenancePage />;
  }

  return (
    <I18nProvider
      currency={userCurrency}
      language={language}
      messages={messages}
    >
      <EdsNumberFormatProvider>
        <AuthSessionBootstrap
          currentUser={currentUser}
          onCurrentUserFetchFailed={handleCurrentUserFetchFailed}
          onSubdomainResolved={setSubdomain}
        />
        {content}
      </EdsNumberFormatProvider>
    </I18nProvider>
  );
}

export default App;
